import {
  ReactNode,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  useEffect,
} from 'react';

import { META_COLLECT_API } from '../../../api';
import { ClosedCollect } from '../../../types/ClosedCollect';
import { IGetPrtinClosedCollect } from '../../../types/PrintClosedCollect';
import { deformatDate } from '../../../utils/date/deformatDate';
import { useSigninContext } from '../../AuthProviderPages/Signin';
import { usePageContext } from '../../usePage';

interface IContextProps {
  children: ReactNode;
}

interface IContext {
  openSideModalFilterClosedCollect: boolean;
  openSideModalFilterClosedCollectAction: () => void;

  DFNOMECARRETEIRO: string;
  setDFNOMECARRETEIRO: Dispatch<SetStateAction<string>>;
  DFORDEMCOLETA: string;
  setDFORDEMCOLETA: Dispatch<SetStateAction<string>>;
  DFNOMELINHA: string;
  setDFNOMELINHA: Dispatch<SetStateAction<string>>;
  DFPLACAVEICULO: string;
  setDFPLACAVEICULO: Dispatch<SetStateAction<string>>;
  DFDATACOLETA: string;
  setDFDATACOLETA: Dispatch<SetStateAction<string>>;
  DFDATASAIDA: string;
  setDFDATASAIDA: Dispatch<SetStateAction<string>>;
  DFIDCOLETA: string;
  setDFIDCOLETA: Dispatch<SetStateAction<string>>;
  DFBLOQUEIOERP: string;
  setDFBLOQUEIOERP: Dispatch<SetStateAction<string>>;
  DFTOTALARMAZENADO: string;
  setDFTOTALARMAZENADO: Dispatch<SetStateAction<string>>;
  DFDATAPROGRAMADA: string;
  setDFDATAPROGRAMADA: Dispatch<SetStateAction<string>>;

  filterClosedCollectAction: () => void;
  cleanAllInputsAction: () => void;
  loadClosedCollectAction: () => void;

  openInfo: boolean;
  setOpenInfo: Dispatch<SetStateAction<boolean>>;

  selectedClosedCollect: ClosedCollect | undefined;
  setSelectedClosedCollect: Dispatch<SetStateAction<ClosedCollect | undefined>>;

  closedCollect: ClosedCollect[];
  setClosedCollect: Dispatch<SetStateAction<ClosedCollect[]>>;

  loadingClosedCollect: boolean;

  offset: number;
  setOfsset: Dispatch<SetStateAction<number>>;
  limit: number;
  setPage: Dispatch<SetStateAction<number>>;
  page: number;
  quantity: number;
  setHasClick: Dispatch<SetStateAction<boolean>>;
  start: number;
  setStart: Dispatch<SetStateAction<number>>;
  openFirstModel: boolean;
  setOpenFirstModel: Dispatch<SetStateAction<boolean>>;
  openSecondModel: boolean;
  setOpenSecondModel: Dispatch<SetStateAction<boolean>>;
  printClosedCollect: IGetPrtinClosedCollect | undefined;
  setPrintClosedCollect: Dispatch<
    SetStateAction<IGetPrtinClosedCollect | undefined>
  >;
}

const Context = createContext({} as IContext);

const ClosedCollectContextProvider = ({ children }: IContextProps) => {
  const [DFNOMECARRETEIRO, setDFNOMECARRETEIRO] = useState<string>('');
  const [DFORDEMCOLETA, setDFORDEMCOLETA] = useState<string>('');
  const [DFNOMELINHA, setDFNOMELINHA] = useState<string>('');
  const [DFPLACAVEICULO, setDFPLACAVEICULO] = useState<string>('');
  const [DFDATACOLETA, setDFDATACOLETA] = useState<string>('');
  const [DFDATASAIDA, setDFDATASAIDA] = useState<string>('');
  const [DFIDCOLETA, setDFIDCOLETA] = useState<string>('');
  const [DFBLOQUEIOERP, setDFBLOQUEIOERP] = useState<string>('');
  const [DFTOTALARMAZENADO, setDFTOTALARMAZENADO] = useState<string>('');
  const [DFDATAPROGRAMADA, setDFDATAPROGRAMADA] = useState<string>('');

  const [loadingClosedCollect, setIsLoadingClosedCollect] =
    useState<boolean>(false);
  const [
    openSideModalFilterClosedCollect,
    setOpenSideModalFilterClosedCollect,
  ] = useState<boolean>(false);
  const [openInfo, setOpenInfo] = useState<boolean>(false);

  const [selectedClosedCollect, setSelectedClosedCollect] = useState<
    ClosedCollect | undefined
  >();
  const [printClosedCollect, setPrintClosedCollect] =
    useState<IGetPrtinClosedCollect>();
  const [closedCollect, setClosedCollect] = useState<ClosedCollect[]>([]);

  const [limit] = useState<number>(50);
  const [offset, setOfsset] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [hasClick, setHasClick] = useState<boolean>(false);
  const { path, user } = useSigninContext();
  const { selectedPage } = usePageContext();
  const [start, setStart] = useState(1);
  const [openFirstModel, setOpenFirstModel] = useState<boolean>(false);
  const [openSecondModel, setOpenSecondModel] = useState<boolean>(false);

  const Limit = `limit=${limit}`;
  const Offset = `offset=${offset}`;
  const NOMECARRETEIRO = `DFNOMECARRETEIRO=${DFNOMECARRETEIRO}`;
  const ORDEMCOLETA = `DFORDEMCOLETA=${DFORDEMCOLETA}`;
  const NOMELINHA = `DFNOMELINHA=${DFNOMELINHA}`;
  const PLACAVEICULO = `DFPLACAVEICULO=${DFPLACAVEICULO}`;
  const DATACOLETA = `DFDATACOLETA=${deformatDate(DFDATACOLETA)}`;
  const DATASAIDA = `DFDATASAIDA=${deformatDate(DFDATASAIDA)}`;
  const IDCOLETA = `DFIDCOLETA=${DFIDCOLETA}`;
  const STATUS = `DFSTATUS=F`;
  const BLOQUEIO = `DFBLOQUEIOERP=${DFBLOQUEIOERP}`;
  const TOTALARMAZENADO = `DFTOTALARMAZENADO=${DFTOTALARMAZENADO}`;
  const DATAPROGRAMADA = `DFDATAPROGRAMADA=${deformatDate(DFDATAPROGRAMADA)}`;
  const IDUNIDADE = user?.DFIDUNIDADE
    ? `&DFIDUNIDADE=${user?.DFIDUNIDADE}`
    : '';
  const openSideModalFilterClosedCollectAction = () => {
    setOpenSideModalFilterClosedCollect(!openSideModalFilterClosedCollect);
  };

  useEffect(() => {
    if (hasClick) {
      setIsLoadingClosedCollect(true);
      META_COLLECT_API.get(
        `/coleta?${Limit}&${Offset}&${STATUS}&${NOMECARRETEIRO}&${ORDEMCOLETA}&${NOMELINHA}&${PLACAVEICULO}&${DATACOLETA}&${DATASAIDA}&${IDCOLETA}&${BLOQUEIO}&${TOTALARMAZENADO}&${DATAPROGRAMADA}${IDUNIDADE}`,
        {
          headers: {
            path,
          },
        },
      )
        .then(response => {
          setClosedCollect(response.data.coletas);
          setQuantity(response.data.quantidade);
          setIsLoadingClosedCollect(false);
          setHasClick(false);
        })
        .catch(() => {
          setIsLoadingClosedCollect(false);
          setHasClick(false);
        });
    }
  }, [hasClick]);

  const loadClosedActionWithParams = () => {
    setIsLoadingClosedCollect(true);
    META_COLLECT_API.get(`/coleta?${Limit}&offset=0&${STATUS}${IDUNIDADE}`, {
      headers: {
        path,
      },
    })
      .then(response => {
        setClosedCollect(response.data.coletas);
        setQuantity(response.data.quantidade);
        setIsLoadingClosedCollect(false);
        setStart(1);
      })
      .catch(() => {
        setIsLoadingClosedCollect(false);
        setStart(1);
      });
  };

  const executeCleanInputs = () => {
    setDFNOMECARRETEIRO('');
    setDFORDEMCOLETA('');
    setDFNOMELINHA('');
    setDFPLACAVEICULO('');
    setDFDATACOLETA('');
    setDFDATASAIDA('');
    setOpenInfo(false);
    setDFBLOQUEIOERP('');
    setDFTOTALARMAZENADO('');
    setDFDATAPROGRAMADA('');
  };

  const cleanAllInputsAction = () => {
    executeCleanInputs();
    openSideModalFilterClosedCollectAction();
    loadClosedActionWithParams();
    setPage(1);
    setStart(1);
  };

  const filterClosedCollectAction = () => {
    setOfsset(0);
    setPage(1);
    openSideModalFilterClosedCollectAction();
    setIsLoadingClosedCollect(true);
    META_COLLECT_API.get(
      `/coleta?${Limit}&offset=0&${STATUS}&${NOMECARRETEIRO}&${ORDEMCOLETA}&${NOMELINHA}&${PLACAVEICULO}&${DATACOLETA}&${DATASAIDA}&${IDCOLETA}&${BLOQUEIO}&${TOTALARMAZENADO}&${DATAPROGRAMADA}${IDUNIDADE}`,
      {
        headers: {
          path,
        },
      },
    )
      .then(response => {
        setClosedCollect(response.data.coletas);
        setQuantity(response.data.quantidade);
        setIsLoadingClosedCollect(false);
        setStart(1);
      })
      .catch(() => {
        setIsLoadingClosedCollect(false);
        setStart(1);
      });
  };

  const loadClosedCollectAction = () => {
    setIsLoadingClosedCollect(true);
    loadClosedActionWithParams();
  };

  useEffect(() => {
    if (selectedPage.option !== 'encerrada') {
      executeCleanInputs();
      setOfsset(0);
      setPage(1);
      setStart(1);
    }
  }, [selectedPage]);

  useEffect(() => {
    if (user?.DFIDUNIDADE) {
      loadClosedCollectAction();
    }
  }, [user]);

  return (
    <Context.Provider
      value={{
        cleanAllInputsAction,
        DFORDEMCOLETA,
        filterClosedCollectAction,
        loadClosedCollectAction,
        limit,
        loadingClosedCollect,
        offset,
        openInfo,
        openSideModalFilterClosedCollect,
        openSideModalFilterClosedCollectAction,
        page,
        quantity,
        setHasClick,
        setOfsset,
        setOpenInfo,
        setPage,
        setClosedCollect,
        DFDATACOLETA,
        DFDATASAIDA,
        DFNOMECARRETEIRO,
        DFNOMELINHA,
        DFPLACAVEICULO,
        DFIDCOLETA,
        closedCollect,
        selectedClosedCollect,
        setDFDATACOLETA,
        setDFDATASAIDA,
        setDFNOMECARRETEIRO,
        setDFNOMELINHA,
        setDFORDEMCOLETA,
        setDFPLACAVEICULO,
        setSelectedClosedCollect,
        setDFIDCOLETA,
        start,
        setStart,
        DFBLOQUEIOERP,
        DFTOTALARMAZENADO,
        setDFBLOQUEIOERP,
        setDFTOTALARMAZENADO,
        DFDATAPROGRAMADA,
        setDFDATAPROGRAMADA,
        openFirstModel,
        openSecondModel,
        setOpenFirstModel,
        setOpenSecondModel,
        printClosedCollect,
        setPrintClosedCollect,
      }}
    >
      {children}
    </Context.Provider>
  );
};

function useClosedCollectContext() {
  const context = useContext(Context);
  return context;
}

export { ClosedCollectContextProvider, useClosedCollectContext };
